import { useState, useEffect, useRef } from 'react';

/**
 * Shared navigation component for DS ecosystem apps
 * TypeScript version with DSAccount integration
 */

interface User {
  id?: string;
  name?: string;
  email: string;
}

interface App {
  id: string;
  name: string;
  url: string;
  status: string;
}

interface DSNavProps {
  user: User | null;
  onLogout: () => void;
  currentAppId?: string;
  appName?: string;
  registryUrl?: string;
}

export function DSNav({
  user,
  onLogout,
  currentAppId = 'dscrm',
  appName = 'DSCRM',
  registryUrl = 'https://registry.digistratum.com'
}: DSNavProps): JSX.Element {
  const [apps, setApps] = useState<App[]>([]);
  const [showAppSwitcher, setShowAppSwitcher] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const appSwitcherRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Fetch apps from registry
  useEffect(() => {
    const fetchApps = async () => {
      try {
        const res = await fetch(`${registryUrl}/api/apps`);
        if (res.ok) {
          const data = await res.json();
          const productionApps = data.filter((app: App) =>
            app.status === 'production' && app.url
          );
          setApps(productionApps);
        }
      } catch (err) {
        console.error('Failed to fetch apps:', err);
        // Fallback to hardcoded apps if registry is unavailable
        setApps([
          { id: 'dsaccount', name: 'DS Account', url: 'https://account.digistratum.com', status: 'production' },
          { id: 'dskanban', name: 'DS Kanban', url: 'https://taskmgt.digistratum.com', status: 'production' },
          { id: 'dscrm', name: 'DSCRM', url: 'https://crm.digistratum.com', status: 'production' },
        ]);
      }
    };
    fetchApps();
  }, [registryUrl]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (appSwitcherRef.current && !appSwitcherRef.current.contains(e.target as Node)) {
        setShowAppSwitcher(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    setShowUserMenu(false);
    onLogout();
  };

  const userInitial = (user?.name || user?.email || 'U')[0].toUpperCase();

  return (
    <nav className="h-14 bg-white border-b border-gray-200 px-4 flex items-center justify-between">
      <div className="flex items-center gap-4">
        {/* App Switcher */}
        <div className="relative" ref={appSwitcherRef}>
          <button
            className="p-2 rounded-md hover:bg-gray-100 text-gray-600"
            onClick={() => setShowAppSwitcher(!showAppSwitcher)}
            aria-label="Switch apps"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <rect x="2" y="2" width="6" height="6" rx="1" />
              <rect x="12" y="2" width="6" height="6" rx="1" />
              <rect x="2" y="12" width="6" height="6" rx="1" />
              <rect x="12" y="12" width="6" height="6" rx="1" />
            </svg>
          </button>

          {showAppSwitcher && (
            <div className="absolute left-0 top-full mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
              <div className="px-4 py-2 text-sm font-medium text-gray-500 border-b">
                DigiStratum Apps
              </div>
              {apps.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-400">Loading apps...</div>
              ) : (
                <ul className="py-2">
                  {apps.map(app => (
                    <li key={app.id}>
                      <a
                        href={app.url}
                        className={`flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-50 ${
                          app.id === currentAppId ? 'text-ds-primary font-medium' : 'text-gray-700'
                        }`}
                      >
                        {app.name}
                        {app.id === currentAppId && (
                          <span className="text-xs bg-blue-100 text-ds-primary px-2 py-0.5 rounded">
                            Current
                          </span>
                        )}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
              <div className="px-4 py-2 border-t">
                <a
                  href="https://account.digistratum.com"
                  className="text-sm text-ds-primary hover:underline"
                >
                  DS Account →
                </a>
              </div>
            </div>
          )}
        </div>

        {/* App Name */}
        <span className="font-semibold text-gray-900">{appName}</span>
      </div>

      <div className="flex items-center">
        {/* User Menu */}
        {user && (
          <div className="relative" ref={userMenuRef}>
            <button
              className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-gray-100"
              onClick={() => setShowUserMenu(!showUserMenu)}
            >
              <span className="w-8 h-8 rounded-full bg-ds-primary text-white flex items-center justify-center text-sm font-medium">
                {userInitial}
              </span>
              <span className="text-sm text-gray-700 hidden sm:block">
                {user.name || user.email}
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" className="text-gray-400">
                <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
              </svg>
            </button>

            {showUserMenu && (
              <div className="absolute right-0 top-full mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <div className="p-4 flex items-center gap-3">
                  <span className="w-12 h-12 rounded-full bg-ds-primary text-white flex items-center justify-center text-lg font-medium">
                    {userInitial}
                  </span>
                  <div>
                    <div className="font-medium text-gray-900">{user.name || 'User'}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </div>
                </div>
                <div className="border-t" />
                <a
                  href="https://account.digistratum.com"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Manage Account
                </a>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50"
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

export default DSNav;
